import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Search, Sun, Moon, Home, Calendar, Star } from "react-feather";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import CustomerPicker from "../../sales/components/CustomerPicker";
import { useEmployeePhotoCache } from "../../masters/employees/useEmployeePhotoCache";
import { useCanViewRemarks } from "../../masters/employees/useEmployeePermissions";
import GuardGradingPanel from "../../masters/employees/components/GuardGradingPanel";
import { useLazyGetGuardLookupQuery } from "../guardingApi";
import GuardLookupTabs from "../components/GuardLookupTabs";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";

const SHIFT_OPTIONS = [
  { value: "", label: "All" },
  { value: "Day", label: "Day", icon: Sun },
  { value: "Night", label: "Night", icon: Moon },
];

const shiftCls = (shift) => (shift === "Day" ? "" : shift === "Night" ? "shift-night" : "shift-unknown");

const initialsOf = (empName, empLastName) => {
  const a = (empName || "").trim().charAt(0);
  const b = (empLastName || "").trim().charAt(0);
  return (a + b).toUpperCase() || "?";
};

// Real captured photo where one exists, initials otherwise - same photo the
// employee's biometric enrollment captured (Batch67's combined photo +
// fingerprint capture), already used this way on the Attendance Dashboard
// and AI Guard Roster cards. Guard Lookup's own DTO carries no HasPhoto flag
// (unlike the Roster candidates one), so `photoUrl` is undefined (still
// resolving/no fetch attempted), null (fetch resolved to no photo - 404), or
// an object URL (loaded) - see useEmployeePhotoCache below, which fetches once
// per unique employeeRecordGuid and hands every card the same object URL,
// mirroring the Flutter screen's shared employeePhotoProvider family cache
// instead of each card re-fetching/re-decoding the same blob.
const GuardCard = ({ row, photoUrl, onGrade }) => {
  const cls = shiftCls(row.shift);
  const ShiftIcon = row.shift === "Night" ? Moon : Sun;
  return (
    <div className="guard-lookup-card">
      <div className={`guard-lookup-shift-bar ${cls}`} />
      <div className="guard-lookup-card-body">
        <div className="d-flex align-items-center gap-2 mb-2">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="guard-lookup-avatar-img" />
          ) : (
            <div className="guard-lookup-avatar">{initialsOf(row.empName, row.empLastName)}</div>
          )}
          <div className="min-width-0 flex-fill" style={{ minWidth: 0 }}>
            <div className="fw-semibold text-truncate" title={`${row.empName || ""} ${row.empLastName || ""}`}>
              {row.empName} {row.empLastName}
            </div>
            <div className="guard-lookup-empcode">#{row.empCode}</div>
          </div>
          {onGrade && (
            <button
              type="button"
              className="guard-lookup-grade-btn"
              title="Grade / add remark"
              onClick={() => onGrade(row)}
            >
              <Star size={14} />
            </button>
          )}
        </div>
        <div className="d-flex align-items-center gap-1 text-truncate mb-1" title={row.custName || ""}>
          <Home size={12} className="text-muted flex-shrink-0" />
          <span className="small text-truncate">{row.custName || "Unknown customer"}</span>
        </div>
        {(row.area || row.assignmentName) && (
          <div className="small text-muted text-truncate mb-2" title={row.area || row.assignmentName || ""}>
            {row.assignmentName || row.area}
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between mt-2">
          <div className="d-flex align-items-center gap-1 small text-muted">
            <Calendar size={11} />
            {row.attendanceDate ? new Date(row.attendanceDate).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "-"}
          </div>
          <span className={`guard-lookup-shift-pill ${cls}`}>
            <ShiftIcon size={11} /> {row.shift || "-"}
          </span>
        </div>
        {row.matchMethod && <div className="guard-lookup-match-tag mt-1">{row.matchMethod}</div>}
      </div>
    </div>
  );
};

// React port of the Flutter Attendance module's Guard Lookup screen
// (2026-09-05), surfaced under the Guarding menu since that's where a
// supervisor would look for "who guarded this customer, in this date
// range". Same two-step UX as the mobile screen: pick a customer + date
// range and Search (one server round trip against Batch126's
// sp_attendance_guard_lookup), then filter the already-fetched result set
// locally by guard name/code and Day/Night shift - no extra round trip for
// that part.
const GuardLookupPage = () => {
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_7_DAYS");
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  // Employee ID (exact) or name (partial), sent to the server (Batch214, 2026-10-22) -
  // finds a guard without picking a customer first.
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [localFilter, setLocalFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  // The server's customerSearch param is a partial LIKE match by design
  // (Batch126: "Searched by customer name (partial match)") - picking
  // "Texas Alarms" would otherwise also return "Texas Alarms Branch 2" etc.
  // CustomerPicker gives an exact pick though, so the exact name used for
  // THIS search is captured here and applied as a client-side exact filter,
  // same convention already used on the Stock Movement Report page for the
  // identical ambiguity. Captured at search time (not read live off
  // selectedCustomerOption) so changing the picker without hitting Search
  // again doesn't retroactively change what's on screen.
  const [searchedCustomerName, setSearchedCustomerName] = useState("");
  // Quick-grade popup (2026-09-05, direct instruction) - just the identity
  // header + Current Grade + Add Remark form from Guard Remarks & Grading
  // (GuardGradingPanel, showTimeline off), not the full page - "only the
  // top section... as a small popup". Gated on Employees.ViewRemarks, a
  // permission entirely independent of this page's own Attendance.Mark/
  // Attendance.View gate, so the button only renders for someone who'd
  // actually be able to use it.
  const canViewRemarks = useCanViewRemarks();
  const [gradingRow, setGradingRow] = useState(null);

  const [triggerSearch, { data, isFetching, error }] = useLazyGetGuardLookupQuery();

  // Header branch (2026-10-02): the search is sent with it, the customer picker keeps
  // to it, and switching it re-runs a search already on screen - or, when the picked
  // customer belongs to another branch, clears the pick and the results instead.
  const { selectedBranchId } = useBranchFilter();
  const searchedBranch = useRef(undefined);
  useEffect(() => {
    if (!hasSearched || searchedBranch.current === undefined || searchedBranch.current === selectedBranchId) return;
    const pickedBranch = selectedCustomerOption?.customer?.branchId;
    if (selectedBranchId != null && pickedBranch != null && pickedBranch !== selectedBranchId) {
      searchedBranch.current = selectedBranchId;
      setSelectedCustomerOption(null);
      setHasSearched(false);
      return;
    }
    runSearch();
  }, [selectedBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each guard's enrollment photo (GET /employees/{guid}/photo, already authorized
  // for Attendance.Mark/Attendance.View), fetched once per guid however many
  // date/shift rows they appear on. The cache itself moved into
  // useEmployeePhotoCache when the Attendance Dashboard was ported and needed the
  // identical behaviour - same semantics as the copy that used to live here.
  const photoUrls = useEmployeePhotoCache(data);

  const runSearch = () => {
    const customerName = selectedCustomerOption?.customer?.custName || "";
    setHasSearched(true);
    setSearchedCustomerName(customerName);
    searchedBranch.current = selectedBranchId;
    triggerSearch({
      customerSearch: customerName || undefined,
      employeeSearch: employeeSearch.trim() || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      branchId: selectedBranchId ?? undefined,
    });
  };

  const results = data || [];
  const filteredResults = useMemo(() => {
    const term = localFilter.trim().toLowerCase();
    return (data || [])
      .filter((r) => !searchedCustomerName || (r.custName || "").toLowerCase() === searchedCustomerName.toLowerCase())
      .filter((r) => !shiftFilter || r.shift === shiftFilter)
      .filter(
        (r) =>
          !term ||
          `${r.empName || ""} ${r.empLastName || ""}`.toLowerCase().includes(term) ||
          String(r.empCode || "").includes(term)
      );
  }, [data, localFilter, shiftFilter, searchedCustomerName]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Guard Lookup</h4>
              <h6>Search a customer or an employee ID / name and a date range to see who guarded where</h6>
            </div>
          </div>
        </div>

        <GuardLookupTabs />

        <div className="card">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">Customer</label>
                <CustomerPicker
                  key={selectedBranchId ?? "all"}
                  branchId={selectedBranchId}
                  value={selectedCustomerOption}
                  onChange={setSelectedCustomerOption}
                  menuPortalTarget={document.body}
                  isClearable
                />
              </div>
              <div className="col-12 col-md-2">
                <label className="form-label small text-muted mb-1">Employee ID / Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. 66296"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                />
              </div>
              <div className="col-12 col-md-5">
                <label className="form-label small text-muted mb-1">Date Range</label>
                <DateRangePicker
                  preset={preset}
                  setPreset={setPreset}
                  dateFrom={dateFrom}
                  setDateFrom={setDateFrom}
                  dateTo={dateTo}
                  setDateTo={setDateTo}
                />
              </div>
              <div className="col-12 col-md-2">
                <button type="button" className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-1" onClick={runSearch} disabled={isFetching}>
                  {isFetching ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <>
                      <Search size={16} /> Search
                    </>
                  )}
                </button>
              </div>
            </div>

            {hasSearched && (
              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 260 }}
                  placeholder="Filter by guard name or code..."
                  value={localFilter}
                  onChange={(e) => setLocalFilter(e.target.value)}
                />
                <div className="btn-group" role="group">
                  {SHIFT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-sm rounded-pill me-1 ${shiftFilter === opt.value ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setShiftFilter(opt.value)}
                    >
                      {opt.icon ? <opt.icon size={13} className="me-1" /> : null}
                      {opt.label}
                    </button>
                  ))}
                </div>
                <span className="small text-muted ms-auto">{filteredResults.length} of {results.length} shown</span>
              </div>
            )}
          </div>
        </div>

        {!hasSearched && !isFetching && (
          <div className="text-center text-muted py-5">
            <Search size={40} className="mb-2 opacity-50" />
            <div>Search a customer or an employee ID / name and a date range to see who guarded where.</div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger mt-3">
            {error?.data?.message || "Something went wrong while searching. Please try again."}
          </div>
        )}

        {hasSearched && !isFetching && !error && (
          filteredResults.length === 0 ? (
            <div className="text-center text-muted py-5">
              {results.length === 0 ? "No records for this search." : "No guards match that filter."}
            </div>
          ) : (
            <div className="guard-lookup-grid mt-3">
              {filteredResults.map((row, idx) => (
                <GuardCard
                  key={`${row.employeeRecordGuid}-${row.attendanceDate}-${row.shift}-${idx}`}
                  row={row}
                  photoUrl={photoUrls[row.employeeRecordGuid]}
                  onGrade={canViewRemarks ? setGradingRow : undefined}
                />
              ))}
            </div>
          )
        )}
      </div>

      {gradingRow && (
        <Modal show onHide={() => setGradingRow(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Grade / Remarks</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <GuardGradingPanel
              employee={{
                recordGuid: gradingRow.employeeRecordGuid,
                empName: gradingRow.empName,
                empLastName: gradingRow.empLastName,
                empCode: gradingRow.empCode,
                branchCode: gradingRow.branchCode,
              }}
              showTimeline={false}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default GuardLookupPage;
